/**
 * The Chief of Staff conversation: messages streamed over /chat, the Manager's plans as live cards, charts, forms the
 * host app opens, and starters and slash commands the host app supplies. The web dashboard and the apps panel in the
 * CRM both render it; the panel adds the page the user is on as context and leaves out uploads and the header.
 */
import { useState, useImperativeHandle, forwardRef, useEffect, useRef, type ReactNode } from 'react';
import { authenticatedFetch } from '@diggens/api-client';
import { Box, TextField, Button, Paper, Typography, CircularProgress, Chip, IconButton } from '@mui/material';
import { TypingIndicator } from './TypingIndicator';
import { Zap, Rocket, Send, Paperclip } from 'lucide-react';
import { uploadFile } from '@diggens/api-client';
import { Alert } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import type { ChatAction, ChatMessage, PageContext, RemovalCard as Removal } from '@diggens/types';
import { ChartRenderer } from './ChartRenderer';
import { SLASH_MENU_ID, SlashMenu, useSlashMenu } from './SlashMenu';
import type { SlashCommand } from './slash';
import { StarterCards } from './StarterCards';
import type { Starter } from './starters';
import { PlanCard, applyPlanEvent, type PlanCardState } from './PlanCard';
import { ICON_STROKE } from './icons';
import { MARKDOWN_TABLE_SX, REMARK_PLUGINS } from './markdown';
import { useChatStream } from './useChatStream';
import { RemovalCard } from './RemovalCard';

type Message = ChatMessage;

export const MESSAGE_PLACEHOLDER = 'Message your Chief of Staff, or type / for commands';

/** Shows a removal card under the reply it came with, the last agent message, or as a message of its own when there is none. */
function withRemovalCard(messages: Message[], card: Removal): Message[] {
  const last = messages.at(-1);
  if (last?.role === 'agent' && !last.removalCard) return [...messages.slice(0, -1), { ...last, removalCard: card }];
  return [...messages, { role: 'agent', content: '', timestamp: new Date(), removalCard: card }];
}

export interface ChatClientProps {
  tenantId: string;
  userId: string;
  userName: string;
  onActionTriggered: (action: ChatAction | null) => void;
  /**
   * Where in the apps the user is, sent with every message so "add a milestone to this project" resolves against the
   * record on screen. A hint only; the server takes the tenant and user from the token.
   */
  pageContext?: PageContext | null;
  /** Whether the chat draws its own "Chief of Staff" header; a host with its own header turns it off. Default true. */
  showHeader?: boolean;
  /** Whether documents can be dropped or attached for the Chief of Staff to process. Default true. */
  uploads?: boolean;
  /** Sent as the user's first message into the new conversation the chat opens on. */
  initialPrompt?: string;
  /** Offered while the conversation is empty; one that sends is sent as the user's message, others go to `onStarterPicked`. */
  starters?: Starter[];
  /** What the starters draw on is still loading: the greeting shows with a spinner in their place. */
  startersLoading?: boolean;
  onStarterPicked?: (starter: Starter) => void;
  /** Listed when the user types "/" in the message box. */
  slashCommands?: SlashCommand[];
  /** The conversation on screen changed: one was saved, opened, or a new one started (null). */
  onConversationChange?: (conversationId: string | null) => void;
  /** A reply finished and the server saved its conversation, which may be new or newly titled. */
  onReplySaved?: (conversationId: string) => void;
  /** The person decided a removal card, which may have removed one of their preferences. */
  onPreferencesChanged?: () => void;
  /** Shown at the right of the header. */
  headerAction?: ReactNode;
}

export interface ChatClientRef {
  notifyAgent: (message: string, fileMetadata?: any) => void;
  getUploadedFile: () => any | null;
  startNewConversation: () => void;
  loadConversation: (conversationId: string) => void;
}

export const ChatClient = forwardRef<ChatClientRef, ChatClientProps>(({ tenantId, userId, userName, onActionTriggered, pageContext, showHeader = true, uploads = true, initialPrompt, starters, startersLoading, onStarterPicked, slashCommands, onConversationChange, onReplySaved, onPreferencesChanged, headerAction }, ref) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // A picked slash command selects the part of its message to complete, once the message box shows it
  const [pendingSelection, setPendingSelection] = useState<[number, number] | null>(null);
  const slash = useSlashMenu(input, slashCommands ?? [], (text, selection) => {
    setInput(text);
    setPendingSelection(selection);
  });
  useEffect(() => {
    if (!pendingSelection || !inputRef.current) return;
    inputRef.current.focus();
    inputRef.current.setSelectionRange(pendingSelection[0], pendingSelection[1]);
    setPendingSelection(null);
  }, [pendingSelection]);
  const [conversationId, setConversationId] = useState<string | null>(null); // Track current conversation
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFileMetadata, setUploadedFileMetadata] = useState<any>(null);
  const [streamingMessage, setStreamingMessage] = useState<string>('');
  const [isStreamingActive, setIsStreamingActive] = useState(false);
  const streamingMessageRef = useRef<string>('');
  // The reply being streamed lives in the ref, which the stream handlers read at once, and in state, which renders it
  const resetStreamingMessage = () => {
    streamingMessageRef.current = '';
    setStreamingMessage('');
  };
  // Plans the Manager proposed, kept up to date by the stream; a message shows them through its show_plan actions
  const [plans, setPlans] = useState<Record<string, PlanCardState>>({});
  // Plans proposed during the reply being streamed, shown before the reply finishes
  const [streamingPlanIds, setStreamingPlanIds] = useState<string[]>([]);

  // Streaming chat hook
  const chatStream = useChatStream({
    onToken: (token: string) => {
      // Accumulate in the ref first: when the last tokens and the actions event arrive in one chunk, onActions runs
      // before React applies these updates, and it must still see every token
      streamingMessageRef.current += token;
      setStreamingMessage(streamingMessageRef.current);
    },

    onReplace: (content: string) => {
      // Backend detected a JSON response (e.g. from Analytics handoff) — replace the raw JSON
      // that was streamed as tokens with the actual text response
      setStreamingMessage(content);
      streamingMessageRef.current = content;
    },

    onActions: (actions: any[]) => {
      // Actions received after text completes
      const allActions = Array.isArray(actions) ? actions : [];
      const action = allActions.length > 0 ? allActions[0] : undefined;

      // Use ref to get the latest streaming message (avoids stale closure)
      // Strip any raw ---ACTIONS--- block that leaked into the streamed text
      let finalMessage = streamingMessageRef.current;
      const actionsIdx = finalMessage.indexOf('---ACTIONS---');
      if (actionsIdx !== -1) {
        finalMessage = finalMessage.substring(0, actionsIdx).trim();
      }

      // Add final agent message with accumulated text
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          content: finalMessage,
          timestamp: new Date(),
          action,
          actions: allActions,
        },
      ]);

      // Reset streaming state
      resetStreamingMessage();
      setStreamingPlanIds([]);
      setIsStreamingActive(false);

      // Notify parent of action
      if (action) {
        onActionTriggered(action);
      }
    },

    onComplete: (conversationId: string) => {
      // Store conversation_id from response
      setConversationId(conversationId);
      onConversationChange?.(conversationId);
      onReplySaved?.(conversationId);
    },

    onError: (error: string) => {
      console.error('Chat stream error:', error);
      setUploadError(`Chat error: ${error}`);
      resetStreamingMessage();
      setStreamingPlanIds([]);
      setIsStreamingActive(false);
    },

    onDetachedComplete: (conversationId: string) => {
      // A conversation the user left mid-reply was saved; it may be new, so the list should hear of it
      onReplySaved?.(conversationId);
    },

    onPlanEvent: (event) => {
      setPlans((prev) => applyPlanEvent(prev, event));
      if (event.type === 'plan_proposed') setStreamingPlanIds((prev) => [...prev, event.planId]);
    },

    // Comes after the actions event, so the reply it belongs to is already a finished message
    onRemovalCard: (card) => setMessages((prev) => withRemovalCard(prev, card)),
  });

  // A removal card's new state, as the API answered the person's click
  const showDecidedRemoval = (card: Removal) => {
    setMessages((prev) => prev.map((message) => (message.removalCard?.id === card.id ? { ...message, removalCard: card } : message)));
    onPreferencesChanged?.();
  };

  /**
   * Sends `text` as the user's message and streams the Manager's reply. `typed` marks what the person typed in the chat
   * box, the only text a preference is saved from; a starter or a prompt from the nav is the app's words.
   */
  const sendText = (text: string, typed = false) => {
    // Add user message
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: text,
        timestamp: new Date(),
      },
    ]);

    // Start streaming
    setIsStreamingActive(true);
    resetStreamingMessage();

    chatStream.sendMessage({
      message: text,
      tenant_id: tenantId,
      user_id: userId,
      user_name: userName,
      conversation_id: conversationId || undefined,
      ...(pageContext ? { page_context: pageContext } : {}),
      ...(typed ? { typed: true as const } : {}),
    });
  };

  const handleSend = () => {
    if (!input.trim()) return;
    sendText(input, true);
    setInput('');
  };

  // File upload handler
  const handleFileUpload = async (file: File) => {
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];

    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a PDF or Excel file');
      return;
    }

    if (file.size > 52428800) {
      setUploadError('File size must be under 50MB');
      return;
    }

    setUploadingFile(true);
    setUploadError(null);
    const visit = visitRef.current;

    try {
      const timestamp = Date.now();
      const tempPath = `${tenantId}/chat-uploads/${timestamp}-${file.name}`;

      const { key } = await uploadFile('portfolio-imports', tempPath, file);
      // The user opened another conversation while it uploaded; sending it there would mix the two, so say it was not sent
      if (visitRef.current !== visit) {
        setUploadError(`${file.name} was not sent, because you opened another conversation while it uploaded.`);
        return;
      }

      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: `📎 Uploaded: ${file.name}`,
          timestamp: new Date(),
        },
      ]);

      const metadata = {
        name: file.name,
        size: file.size,
        type: file.type,
        temp_storage_path: key,
        bucket: 'portfolio-imports',
      };

      console.log('Storing file metadata in client state:', metadata);
      setUploadedFileMetadata(metadata);

      console.log('Sending file metadata to API:', metadata);

      setIsStreamingActive(true);
      resetStreamingMessage();

      chatStream.sendMessage({
        message: `I want to upload a document: ${file.name}`,
        tenant_id: tenantId,
        user_id: userId,
        user_name: userName,
        conversation_id: conversationId || undefined,
        fileMetadata: metadata,
      });
    } catch (error) {
      console.error('Upload error:', error);
      // An upload the user left behind fails quietly, not in the conversation they opened since
      if (visitRef.current === visit) setUploadError(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const notifyAgent = (message: string, fileMetadata?: any) => {
    // Add system message to UI immediately
    setMessages((prev) => [
      ...prev,
      {
        role: 'system',
        content: message,
        timestamp: new Date(),
      },
    ]);

    setIsStreamingActive(true);
    resetStreamingMessage();

    chatStream.sendMessage({
      message,
      tenant_id: tenantId,
      user_id: userId,
      user_name: userName,
      conversation_id: conversationId || undefined,
      fileMetadata,
      ...(pageContext ? { page_context: pageContext } : {}),
    });
  };

  // The conversation a history load is for; one that finishes after the user moved on is ignored
  const loadingConversationRef = useRef<string | null>(null);
  // Counts each time the user leaves a conversation; an upload that finishes after it changed belongs to none on screen
  const visitRef = useRef(0);

  /**
   * Detaches a reply still streaming into the conversation on screen before another takes its place, so it cannot land
   * in the next one. The server finishes and saves the reply, which shows when that conversation is opened again.
   */
  const leaveConversation = () => {
    visitRef.current += 1;
    chatStream.detachStream();
    resetStreamingMessage();
    setStreamingPlanIds([]);
    setPlans({});
    setIsStreamingActive(false);
    setUploadError(null);
    // A file uploaded in the conversation left belongs to it, not to a form the next one opens
    setUploadedFileMetadata(null);
  };

  const startNewConversation = () => {
    leaveConversation();
    loadingConversationRef.current = null;
    setConversationId(null);
    setMessages([]);
    onConversationChange?.(null);
  };

  const loadConversation = async (convId: string) => {
    leaveConversation();
    loadingConversationRef.current = convId;
    setConversationId(convId);
    setMessages([]);
    onConversationChange?.(convId);

    // Load conversation history from API
    try {
      const response = await authenticatedFetch(`/chat/conversations/${convId}/history?user_id=${userId}`);
      if (response.ok) {
        const history = await response.json();
        if (loadingConversationRef.current !== convId) return;
        // Convert AgentInputItem[] to UI Message[], each removal card, in its current state, under the reply it came with
        const uiMessages = history.reduce((shown: Message[], item: { type?: string; role?: string; card?: Removal }): Message[] => {
          if (item.type === 'removal_card' && item.card) return withRemovalCard(shown, item.card);
          if (item.type !== 'message') return shown;
          return [
            ...shown,
            {
              role: item.role === 'user' ? 'user' : 'agent',
              content: extractMessageContent(item),
              timestamp: new Date(),
            },
          ];
        }, []);
        setMessages(uiMessages);
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
    }
  };

  // Helper to extract text content from AgentInputItem
  const extractMessageContent = (item: any): string => {
    if (item.role === 'user') {
      // User messages have content as string with format:
      // "User ID: xxx\nUser: xxx\nDate: xxx\nMessage: actual message"
      if (typeof item.content === 'string') {
        // Extract just the message part after "Message: "
        const match = item.content.match(/Message: (.+)$/s);
        return match ? match[1] : item.content;
      }
      if (Array.isArray(item.content)) {
        const textPart = item.content.find((c: any) => c.type === 'input_text');
        if (textPart?.text) {
          const match = textPart.text.match(/Message: (.+)$/s);
          return match ? match[1] : textPart.text;
        }
        return textPart?.text || '';
      }
    } else if (item.role === 'assistant') {
      // Assistant messages have content array with output_text
      if (Array.isArray(item.content)) {
        const textPart = item.content.find((c: any) => c.type === 'output_text');
        if (textPart?.text) {
          // Try to parse JSON response
          try {
            const parsed = JSON.parse(textPart.text.replace(/^```(?:json)?\s*\n?/m, '').replace(/\n?```\s*$/m, ''));
            return parsed.response || textPart.text;
          } catch {
            return textPart.text;
          }
        }
      }
    }
    return '';
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    notifyAgent,
    startNewConversation,
    loadConversation,
    getUploadedFile: () => uploadedFileMetadata,
  }));

  // Auto-scroll to bottom when messages or streaming message changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // The chat opens on a new conversation; a prompt from the nav starts it, and past conversations open from the list
  // beside it. Without a prompt it waits for the user: the Manager runs only when asked.
  useEffect(() => {
    if (initialPrompt) sendText(initialPrompt);
  }, []);

  // A chat replaced while its reply streams or a file uploads, as New fund in the nav replaces it, lets both go, so
  // neither can open a form or move the list's highlight over the conversation that replaced it
  useEffect(
    () => () => {
      visitRef.current += 1;
      chatStream.detachStream();
    },
    []
  );

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      onDragOver={(e) => {
        if (!uploads) return;
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        if (e.currentTarget === e.target) {
          setIsDragging(false);
        }
      }}
      onDrop={(e) => {
        if (!uploads) return;
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileUpload(file);
      }}
    >
      {/* Drag-Drop Overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(76, 175, 80, 0.1)',
            border: '2px dashed',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" color="primary">
            Drop document here to upload
          </Typography>
        </Box>
      )}

      {/* Header */}
      {showHeader && (
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6">Chief of Staff</Typography>
          {headerAction}
        </Box>
      )}

      {/* Message List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }} id="chat-messages-container">
        {messages.length === 0 &&
          !isStreamingActive &&
          ((starters && starters.length > 0) || startersLoading ? (
            <StarterCards
              userName={userName}
              starters={starters ?? []}
              loading={startersLoading ?? false}
              disabled={uploadingFile}
              onPick={(starter) => (starter.action.kind === 'send' ? sendText(starter.action.prompt) : onStarterPicked?.(starter))}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
              Ask your Chief of Staff a question or give it a task.
            </Typography>
          ))}
        {messages.map((msg, idx) => (
          <Box key={idx} sx={{ mb: 2, display: 'flex', gap: 1 }}>
            {/* Sender indicator */}
            <Box
              sx={{
                width: 4,
                bgcolor: msg.role === 'user' ? 'primary.main' : 'success.main',
                borderRadius: 1,
                flexShrink: 0,
              }}
            />

            {/* Message content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {msg.role === 'user' ? 'You' : msg.role === 'system' ? 'System' : 'Chief of Staff'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: msg.role === 'user' ? 'action.hover' : msg.role === 'system' ? 'info.lighter' : 'action.selected',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    '& p': { margin: 0, marginBottom: 1, lineHeight: 1.6 },
                    '& p:last-child': { marginBottom: 0 },
                    '& ul, & ol': { marginTop: 0.5, marginBottom: 0.5, paddingLeft: 3 },
                    '& li': { marginBottom: 0.25 },
                    '& strong': { fontWeight: 600 },
                    '& code': {
                      bgcolor: 'action.hover',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.9em',
                      fontFamily: 'monospace',
                    },
                    '& pre': {
                      bgcolor: 'action.hover',
                      p: 1.5,
                      borderRadius: 1,
                      overflow: 'auto',
                      marginTop: 1,
                      marginBottom: 1,
                    },
                    '& pre code': {
                      bgcolor: 'transparent',
                      p: 0,
                    },
                    ...MARKDOWN_TABLE_SX,
                  }}
                >
                  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{msg.content.includes('---ACTIONS---') ? msg.content.substring(0, msg.content.indexOf('---ACTIONS---')).trim() : msg.content}</ReactMarkdown>
                </Box>

                {/* Show options as button cards */}
                {msg.action?.type === 'show_options' && msg.action.options && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                    {msg.action.options.map((option) => (
                      <Button
                        key={option.id}
                        variant="outlined"
                        onClick={() => {
                          // Send the user's choice back to the agent
                          const message = option.id === 'trial' ? 'I want to try it now' : 'I want to set up my fund';
                          setIsStreamingActive(true);
                          resetStreamingMessage();
                          chatStream.sendMessage({
                            message,
                            tenant_id: tenantId,
                            user_id: userId,
                            user_name: userName,
                            conversation_id: conversationId || undefined,
                            ...(pageContext ? { page_context: pageContext } : {}),
                          });
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          p: 2,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2,
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          {option.id === 'trial' ? <Zap size={18} strokeWidth={1} /> : <Rocket size={18} strokeWidth={1} />}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {option.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                    ))}
                  </Box>
                )}

                {msg.action?.type === 'show_form' && <Chip label="📋 View Form" size="small" onClick={() => onActionTriggered(msg.action!)} sx={{ mt: 1 }} color="primary" variant="outlined" />}

                {msg.action?.type === 'show_status' && <Chip label="📊 View Status" size="small" onClick={() => onActionTriggered(msg.action!)} sx={{ mt: 1 }} color="primary" variant="outlined" />}

                {msg.actions?.filter((a) => a.type === 'show_chart').map((chartAction, idx) => (
                  <ChartRenderer key={idx} action={chartAction} />
                ))}

                {msg.actions?.map((a) => (a.type === 'show_plan' && a.plan_id && plans[a.plan_id] ? <PlanCard key={a.plan_id} plan={plans[a.plan_id]!} /> : null))}

                {msg.removalCard && <RemovalCard card={msg.removalCard} onDecided={showDecidedRemoval} />}
              </Paper>
            </Box>
          </Box>
        ))}

        {/* Show streaming message as it arrives */}
        {isStreamingActive && streamingMessage && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Box
              sx={{
                width: 4,
                bgcolor: 'success.main',
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Chief of Staff
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  streaming...
                </Typography>
              </Box>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'action.selected',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    '& p': { margin: 0, marginBottom: 1, lineHeight: 1.6 },
                    '& p:last-child': { marginBottom: 0 },
                    ...MARKDOWN_TABLE_SX,
                  }}
                >
                  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{streamingMessage.includes('---ACTIONS---') ? streamingMessage.substring(0, streamingMessage.indexOf('---ACTIONS---')).trim() : streamingMessage}</ReactMarkdown>
                </Box>
              </Paper>
            </Box>
          </Box>
        )}

        {/* Plans the Manager proposed in this reply, live while it waits for approval or runs steps */}
        {isStreamingActive && streamingPlanIds.map((planId) => (plans[planId] ? <PlanCard key={planId} plan={plans[planId]!} /> : null))}

        {(isStreamingActive && !streamingMessage) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
            <TypingIndicator isTyping={true} />
            <Typography variant="body2" color="text.secondary">
              Diggens is thinking...
            </Typography>
          </Box>
        )}
        {/* Invisible element at the bottom for auto-scroll */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Upload Error */}
      {uploadError && (
        <Box sx={{ px: 2 }}>
          <Alert severity="error" onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.xlsx,.xls"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file);
            e.target.value = '';
          }}
          style={{ display: 'none' }}
        />
        <Box sx={{ display: 'flex', gap: 1, position: 'relative' }}>
          {slash.open && <SlashMenu items={slash.items} current={slash.current} onPick={slash.pick} />}
          {uploads && (
            <IconButton onClick={() => fileInputRef.current?.click()} disabled={uploadingFile || isStreamingActive} size="small" aria-label="Attach a document">
              {uploadingFile ? <CircularProgress size={20} /> : <Paperclip size={20} strokeWidth={ICON_STROKE} />}
            </IconButton>
          )}
          <TextField
            fullWidth
            inputRef={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => slash.onKeyDown(e)}
            // While the slash menu is open, Enter picks a command instead of sending
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && !slash.open && handleSend()}
            placeholder={MESSAGE_PLACEHOLDER}
            disabled={uploadingFile || isStreamingActive}
            size="small"
            slotProps={{
              htmlInput: {
                role: 'combobox',
                'aria-label': 'Message',
                'aria-autocomplete': 'list',
                'aria-expanded': slash.open,
                'aria-controls': slash.open ? SLASH_MENU_ID : undefined,
                'aria-activedescendant': slash.open ? `${SLASH_MENU_ID}-${slash.items[slash.current]?.name}` : undefined,
              },
            }}
          />
          <IconButton onClick={handleSend} disabled={!input.trim() || uploadingFile || isStreamingActive} color="primary">
            <Send size={20} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
});

ChatClient.displayName = 'ChatClient';
